import { inject, Injectable } from '@angular/core';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from '@angular/common/http';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environmentGeneric } from '../../environments/environment.generic';
import { environment } from '../../environments/environment';
import { APPLICATION_ROUTES } from '../constants/application-routes';
import { Router } from '@angular/router';

/**
 * Treat these status codes as unauthorized for login redirect:
 * 0   - Network error or CORS error (often means user is not authenticated or request was blocked)
 * 302 - Redirect (commonly used by Siteminder or similar auth proxies to indicate login is required)
 * 401 - Unauthorized (standard HTTP status for unauthenticated requests)
 * 403 - Forbidden (user is authenticated but not authorized, often triggers re-auth in some flows)
 */
const UNAUTHORIZED_STATUS_CODES: readonly number[] = [0, 302, 401, 403];

@Injectable()
export class AuthRedirectInterceptor implements HttpInterceptor {
    private router = inject(Router);

    private apiEndpoints: string[] = Object.values(environmentGeneric.ZCI_API_ENDPOINTS);

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Only intercept requests to ZCI_API_ENDPOINTS
        const matchesApi = this.apiEndpoints.some((endpoint) => req.url.endsWith(endpoint));

        if (!matchesApi) {
            return next.handle(req);
        }

        return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {
                if (UNAUTHORIZED_STATUS_CODES.includes(error.status)) {
                    this.redirectToLogon();
                    return EMPTY;
                }

                // For client errors (4xx), let them propagate to services for proper handling
                // These are business logic errors (validation, not found, conflict, etc.)
                // that services handle by returning appropriate error enums
                if (error.status >= 400 && error.status < 500) {
                    return throwError(() => error);
                }

                // For server errors (5xx) or other unexpected errors, redirect to tech difficulties page
                // These indicate system unavailability or upstream dependency failures
                this.router.navigate([APPLICATION_ROUTES.TECH_DIFF_REDIRECT]);
                return EMPTY;
            }),
        );
    }

    private redirectToLogon(): void {
        if (environment.isInternal) {
            // For internal environments, redirect to the internal auth endpoint
            window.location.href = `${environment.ZCI_API_BASE_URL}/auth`;
        } else {
            const targetUrl = encodeURIComponent(
                `${environment.APP_URL}${window.location.pathname}`,
            );
            window.location.href = `${environment.LOGON_REDIRECT_URL}${targetUrl}&authenticate=true`;
        }
    }
}
